import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';

/**
 * The one place the connector URLs are spelled out for the public site.
 * login.component.ts imports these rather than keeping its own copies, so a
 * host change (web.askbuddy.ai -> mcp.askliam.ai) is a one-line edit.
 */
export const MCP_URL = 'https://mcp.askliam.ai/mcp';
export const CHATGPT_APP_URL =
    'https://chatgpt.com/plugins/plugin_asdk_app_6a57946a7e008191a57e77c8398f960b?q=LIAM';

/**
 * Opens claude.ai with the "Add custom connector" dialog already up, rather
 * than the connectors list with another button to find and press.
 */
export const CLAUDE_CONNECTOR_URL =
    'https://claude.ai/new?modal=add-custom-connector#settings/customize-connectors';

/**
 * What the connector is called inside Claude. Worth pinning down rather than
 * leaving to each visitor: it is the label they will see on every tool call
 * afterwards, and a connector named "test" is a support ticket later.
 */
export const CONNECTOR_NAME = 'LIAM - Longitudinal Intelligent Agentic Memory';

/**
 * Where visitors open Muse. Muse has no connector form to deep-link into -
 * you ask it in chat and it builds the OAuth handshake itself - so this is
 * just its front door.
 */
export const MUSE_URL = 'https://muse.ai/';

/**
 * The whole setup as one message: the ask and the details together, so Muse
 * has nothing to come back and ask about. Shared with the setup wizard so the
 * dock and the guide never hand out two different versions.
 */
export const MUSE_PASTE_BLOCK =
    `Connect LIAM MCP with below details\nName : ${CONNECTOR_NAME}\nMCP : ${MCP_URL}\nOAuth`;

/**
 * "Your apps" - a dock pinned to the bottom of every public page.
 *
 * The connector pitch used to live two-thirds of the way down the login page,
 * which is a different page from where visitors land. Four actions stood
 * between arriving and seeing the main sell. This puts it on screen at every
 * scroll depth instead, on the story, login, developers and recipes pages.
 *
 * ChatGPT leaves for its directory listing in one click. Claude and Muse
 * both ask the visitor to type something we know and they don't, so those two
 * open a panel first: Claude's shows the two values its dialog wants, Muse's
 * copies the message to send and shows exactly what was copied. The trip to
 * the app is the button at the bottom of each panel.
 */
@Component({
    selector: 'app-connect-apps-dock',
    standalone: true,
    imports: [CommonModule, RouterModule],
    templateUrl: './connect-apps-dock.component.html',
    styleUrls: ['./connect-apps-dock.component.css']
})
export class ConnectAppsDockComponent implements OnInit, OnDestroy {

    constructor(private host: ElementRef<HTMLElement>) { }

    readonly mcpUrl = MCP_URL;
    readonly chatgptAppUrl = CHATGPT_APP_URL;
    readonly claudeConnectorUrl = CLAUDE_CONNECTOR_URL;
    readonly connectorName = CONNECTOR_NAME;
    readonly museUrl = MUSE_URL;
    readonly musePasteBlock = MUSE_PASTE_BLOCK;

    /** Dismissal lasts the tab, not forever - a new visit gets the pitch again. */
    private static readonly DISMISS_KEY = 'liam.dock.dismissed';

    /** Tells the page stylesheet to leave room at the bottom for the dock. */
    private static readonly BODY_CLASS = 'has-apps-dock';

    dismissed = false;

    /** Which app's setup panel is up, if any. */
    openPanel: 'claude' | 'muse' | null = null;
    /** Which value last showed its "Copied" tick. */
    copiedField: 'name' | 'url' | 'muse' | null = null;
    /** False when the browser refused the clipboard, so the panel says "copy it yourself". */
    museCopyOk = true;

    ngOnInit(): void {
        try {
            this.dismissed = sessionStorage.getItem(ConnectAppsDockComponent.DISMISS_KEY) === '1';
        } catch {
            // Private mode / blocked storage: show the dock rather than hide it.
            this.dismissed = false;
        }
        this.syncBodyClass();
    }

    togglePanel(panel: 'claude' | 'muse', event: MouseEvent): void {
        // Without this the document listener below sees the same click and
        // closes the panel in the same tick it was opened.
        event.stopPropagation();
        const opening = this.openPanel !== panel;
        this.openPanel = opening ? panel : null;
        this.copiedField = null;
        // Muse's copy happens on the click that opens the panel - this click
        // is the user gesture the clipboard wants, and the panel then shows
        // what landed there.
        if (opening && panel === 'muse') {
            this.copyMuse();
        }
    }

    /** A click anywhere else, or Escape, puts the panel away. */
    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (this.openPanel && !this.host.nativeElement.contains(event.target as Node)) {
            this.openPanel = null;
        }
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        this.openPanel = null;
    }

    copyField(field: 'name' | 'url', event: MouseEvent): void {
        event.stopPropagation();
        const value = field === 'name' ? this.connectorName : this.mcpUrl;
        navigator.clipboard?.writeText(value).catch(() => { });
        this.flashCopied(field);
    }

    copyMuse(event?: MouseEvent): void {
        event?.stopPropagation();
        if (!navigator.clipboard) {
            this.museCopyOk = false;
            return;
        }
        navigator.clipboard.writeText(this.musePasteBlock).then(
            () => {
                this.museCopyOk = true;
                this.flashCopied('muse');
            },
            () => (this.museCopyOk = false)
        );
    }

    private flashCopied(field: 'name' | 'url' | 'muse'): void {
        this.copiedField = field;
        setTimeout(() => {
            if (this.copiedField === field) {
                this.copiedField = null;
            }
        }, 2000);
    }

    ngOnDestroy(): void {
        document.body.classList.remove(ConnectAppsDockComponent.BODY_CLASS);
    }

    private syncBodyClass(): void {
        document.body.classList.toggle(ConnectAppsDockComponent.BODY_CLASS, !this.dismissed);
    }

    dismiss(): void {
        this.dismissed = true;
        this.syncBodyClass();
        try {
            sessionStorage.setItem(ConnectAppsDockComponent.DISMISS_KEY, '1');
        } catch {
            // Nothing to remember it with - it will be back next navigation.
        }
    }
}
